'use client';

import {
  autoUpdate,
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import classNames from 'classnames';

import { SiteSearchInput, SiteSearchResults } from '@/components/SiteSearch';
import { Typography } from '@/components/Typography';
import { Wrapper } from '@/components/Wrapper';
import MagnifyingGlass from '@/icons/react/MagnifyingGlass';
import X from '@/icons/react/X';
import { useSiteHeader, useSiteSearch } from '@/providers';

interface SiteSearchProps {}

export function SiteSearch({}: SiteSearchProps) {
  const { isOpen, setIsOpen, inputRef } = useSiteSearch();
  const { siteHeaderMainRef } = useSiteHeader();
  const headerRect = siteHeaderMainRef?.current?.getBoundingClientRect();

  const { refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(0)],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  if (!headerRect) return null;

  const yOffset = headerRect.top + headerRect.height;

  function handleClick() {
    setIsOpen(!isOpen);

    setTimeout(() => {
      if (inputRef?.current) {
        inputRef.current.focus();
      }
    }, 1);
  }

  const iconClasses = 'translate-x-px shrink-0 w-6 h-6 md:w-5 md:h-5';

  return (
    <>
      <button
        className={classNames(
          'ml-auto md:ml-0',
          'flex items-center gap-3 rounded-2 transition-colors shrink-0',
          'p-3 md:px-5 md:py-2',
          'bg-paper md:hover:bg-gray-100',
          'md:border md:border-primary',
        )}
        onClick={handleClick}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {isOpen ? (
          <X className={iconClasses} />
        ) : (
          <MagnifyingGlass className={iconClasses} />
        )}

        <Typography name="body-450" className="hidden md:block">
          {isOpen ? 'Close Search' : 'Search 1440'}
        </Typography>
      </button>

      {isOpen && (
        <>
          <FloatingPortal id="floating-ui-portal">
            <FloatingOverlay
              className="bg-black/75"
              lockScroll
              style={{ top: yOffset }}
            />
            <FloatingFocusManager context={context}>
              <div
                className={classNames(
                  'fixed inset-x-0 bg-paper dark:bg-gray-800 pb-10 pt-6',
                )}
                // bump it 24px to cover up the "new" tags on menu items
                style={{ top: yOffset - 24 }}
                ref={refs.setFloating}
                {...getFloatingProps()}
              >
                <Wrapper>
                  <div className="flex flex-col gap-5">
                    <SiteSearchInput />
                    <div>
                      <SiteSearchResults />
                    </div>
                  </div>
                </Wrapper>
              </div>
            </FloatingFocusManager>
          </FloatingPortal>
        </>
      )}
    </>
  );
}
